import React, { useEffect, useState, useCallback, useRef } from 'react';
import api, { apiFetch } from '../services/api';
import VideoPlayer from './VideoPlayer';
import './Lightbox.css';

/**
 * WatermarkedImage for shared gallery.
 * Fetches via the share-token endpoint (no JWT needed).
 */
function WatermarkedImage({ mediaId, token, originalName }) {
  const canvasRef = useRef(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    apiFetch(`/api/share/view/${token}/media/${mediaId}/watermarked`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load image');
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
          if (cancelled) { URL.revokeObjectURL(url); return; }
          const canvas = canvasRef.current;
          if (!canvas) return;
          canvas.width  = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          URL.revokeObjectURL(url);
        };
        img.onerror = () => { URL.revokeObjectURL(url); setLoadError(true); };
        img.src = url;
      })
      .catch(() => { if (!cancelled) setLoadError(true); });

    return () => { cancelled = true; };
  }, [mediaId, token]);

  if (loadError) return <div className="lb-img-error">Could not load image</div>;

  return (
    <canvas
      ref={canvasRef}
      className="lb-canvas"
      onContextMenu={(e) => e.preventDefault()}
      aria-label={originalName}
    />
  );
}

export default function SharedLightbox({ mediaId, token, onClose }) {
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/share/view/${token}/media/${mediaId}`);
      setMedia(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, mediaId]);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  async function handleDownload() {
    if (!media || downloading) return;
    setDownloading(true);
    try {
      if (media.fileType?.startsWith('video')) {
        // Videos: backend redirects to GCS — open link directly
        const baseUrl = process.env.REACT_APP_API_URL || '';
        const a = document.createElement('a');
        a.href = `${baseUrl}/api/share/view/${token}/media/${mediaId}/download`;
        a.download = media.originalName || 'download';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        // Images: proxied through server, fetch into blob
        const res = await apiFetch(`/api/share/view/${token}/media/${mediaId}/download`);
        if (!res.ok) throw new Error('Download failed');
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = media.originalName || 'download';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('[Download]', err);
      setDownloadError('Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  }

  const isImage = media?.fileType?.startsWith('image');

  return (
    <div
      className="lb-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
    >
      <div className="lb-box">
        <button className="lb-close" onClick={onClose} aria-label="Close">✕</button>

        {loading && <div className="lb-loading"><div className="spinner" /></div>}

        {!loading && media && (
          <div className="lb-content">
            {isImage ? (
              <div className="lb-canvas-wrap">
                <WatermarkedImage mediaId={mediaId} token={token} originalName={media.originalName} />
              </div>
            ) : (
              <VideoPlayer hlsUrl={media.hlsUrl} thumbnailUrl={media.thumbnailUrl} />
            )}

            <div className="lb-meta">
              <span className="lb-name">{media.originalName}</span>
              {media.width && media.height && (
                <span className="lb-dim">{media.width} × {media.height}</span>
              )}
              {media.duration && (
                <span className="lb-dim">{Math.round(media.duration)}s</span>
              )}
              {downloadError && (
                <span className="lb-download-error">{downloadError}</span>
              )}
              <button
                className="btn btn-ghost btn-xs lb-download"
                onClick={() => { setDownloadError(''); handleDownload(); }}
                disabled={downloading}
                title="Download original (no watermark)"
              >
                {downloading
                  ? <span className="spinner" style={{ width: 14, height: 14 }} />
                  : '↓ Download'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
